import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  acceptSimulationShelfLife,
  revokeSimulationShelfLife,
} from '../../../api/transport-simulationApi';
import { getApiErrorMessage } from '../../../api/mix-deliveriesApi';
const date = (value) =>
  value?.slice(0, 10).split('-').reverse().join('.') ?? '—';
export default function ShelfLifeAcceptance({
  item,
  run,
  canWrite,
  active = [],
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [expiry, setExpiry] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const agreement = active.find(
    (a) =>
      a.deliveryNumber === item.deliveryNumber &&
      a.materialNumber === item.materialNumber,
  );
  const applied = item.shelfLifeAcceptance;
  const dates = [
    ...new Set((item.shortDatedAlternatives ?? []).map((l) => l.expiresAt)),
  ]
    .sort()
    .reverse();
  const writable = canWrite && run.isCurrent && run.mode !== 'PREVIEW';
  async function save(revoke = false) {
    setBusy(true);
    setError('');
    try {
      const result = revoke
        ? await revokeSimulationShelfLife(agreement.id, run.id)
        : await acceptSimulationShelfLife({
            runId: run.id,
            deliveryNumber: item.deliveryNumber,
            materialNumber: item.materialNumber,
            recipientNumber: item.recipientNumber,
            loadingDate: item.loadingDate,
            minimumExpiry: expiry,
            note: note.trim(),
          });
      setOpen(false);
      onSaved(result);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  if (!dates.length && !applied && !agreement) return null;
  return (
    <Stack spacing={1}>
      {applied && agreement?.id !== applied.id && run.isCurrent && (
        <Alert severity="warning">
          Zgoda zmieniła się lub została cofnięta. Przelicz transporty, aby
          odświeżyć status.
        </Alert>
      )}
      {agreement && !applied && (
        <Alert severity="info">
          Zgoda wymaga przeliczenia albo ponownego zatwierdzenia po zmianie
          danych dostawy.
        </Alert>
      )}
      {(applied || agreement) && (
        <details>
          <summary style={{ cursor: 'pointer' }}>Szczegóły zgody</summary>
          <Typography>
            {(applied || agreement).acceptedBy} ·{' '}
            {new Date((applied || agreement).acceptedAt).toLocaleString(
              'pl-PL',
            )}
          </Typography>
          <Typography>{(applied || agreement).note}</Typography>
          <Typography>
            Zakres: {(applied || agreement).quantity}{' '}
            {(applied || agreement).unit} materiału {item.materialNumber} w
            dostawie {item.deliveryNumber}.
          </Typography>
        </details>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {writable && (
        <Stack direction="row" spacing={1}>
          {dates.length > 0 && (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => {
                setExpiry(dates[0]);
                setNote('');
                setError('');
                setOpen(true);
              }}
            >
              Akceptuj termin dla dostawy
            </Button>
          )}
          {agreement && (
            <Button color="warning" disabled={busy} onClick={() => save(true)}>
              Cofnij zgodę i przelicz
            </Button>
          )}
        </Stack>
      )}
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Akceptacja terminu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Dostawa {item.deliveryNumber} · materiał {item.materialNumber}
            </Typography>
            <Typography>
              Zgoda obejmie {item.acceptanceScopeQuantity ?? item.fullQuantity}{' '}
              {item.unit} tego materiału w tej dostawie, również w innych
              pozycjach. Zmiana odbiorcy, dnia załadunku lub ilości wymaga nowej
              zgody.
            </Typography>
            <TextField
              select
              label="Najkrótszy zaakceptowany termin"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            >
              {dates.map((d) => (
                <MenuItem key={d} value={d}>
                  {date(d)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              multiline
              minRows={2}
              label="Źródło zgody"
              placeholder="Np. mail od klienta z dnia…, osoba akceptująca…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Typography variant="body2">
              Potwierdzasz otrzymanie zgody na krótszy termin. Po zapisaniu
              system przeliczy dostępność.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setOpen(false)}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            disabled={busy || !expiry || note.trim().length < 3}
            onClick={() => save()}
          >
            Zapisz zgodę i przelicz
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
